import { useEffect, useRef } from 'react';
import BookPageImage from '@/components/feature/BookPageImage';

interface ReaderStageProps {
  pages: number[];
  zoom: number;
  flipEnabled: boolean;
  direction: 1 | -1;
  spreadKey: string;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onZoomChange: (zoom: number) => void;
}

const MAX_ZOOM = 3;

export default function ReaderStage({
  pages,
  zoom,
  flipEnabled,
  direction,
  spreadKey,
  canPrev,
  canNext,
  onPrev,
  onNext,
  onZoomChange,
}: ReaderStageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(zoom);
  const zoomChangeRef = useRef(onZoomChange);
  const prevRef = useRef(onPrev);
  const nextRef = useRef(onNext);

  zoomRef.current = zoom;
  zoomChangeRef.current = onZoomChange;
  prevRef.current = onPrev;
  nextRef.current = onNext;

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;

    const distance = (t: TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    let startX = 0;
    let startY = 0;
    let pinching = false;
    let baseDist = 0;
    let baseZoom = 1;
    let moved = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        pinching = true;
        baseDist = distance(e.touches);
        baseZoom = zoomRef.current;
      } else if (e.touches.length === 1) {
        pinching = false;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        moved = false;
      }
    };

    const onMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinching && baseDist > 0) {
        e.preventDefault();
        const ratio = distance(e.touches) / baseDist;
        zoomChangeRef.current(Math.min(MAX_ZOOM, Math.max(1, baseZoom * ratio)));
      } else if (e.touches.length === 1 && !pinching) {
        if (Math.abs(e.touches[0].clientX - startX) > 12 || Math.abs(e.touches[0].clientY - startY) > 12) {
          moved = true;
        }
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (pinching) {
        if (e.touches.length < 2) pinching = false;
        return;
      }
      if (!moved) {
        const t = e.changedTouches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          if (dx > 0) prevRef.current();
          else nextRef.current();
        }
      }
      moved = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, []);

  const isSpread = pages.length > 1;
  const pageClass = isSpread
    ? 'w-[45vw] max-w-[430px] lg:h-[78vh] lg:w-auto lg:max-w-none'
    : 'w-[86vw] max-w-[540px] md:h-[80vh] md:w-auto md:max-w-none';

  const handleDoubleClick = () => {
    onZoomChange(zoomRef.current > 1 ? 1 : 2);
  };

  return (
    <div className="relative flex-1 overflow-hidden bg-primary-950">
      <div ref={stageRef} className="h-full w-full overflow-auto">
        <div className="flex min-h-full w-full items-center justify-center p-3 md:p-6">
          <div
            className="flex items-center justify-center transition-transform duration-200"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
            onDoubleClick={handleDoubleClick}
          >
            <div
              key={spreadKey}
              className={flipEnabled ? (direction > 0 ? 'animate-flip-next' : 'animate-flip-prev') : ''}
            >
              <div className="flex items-center justify-center gap-1 md:gap-2">
                {pages.map((p) => (
                  <BookPageImage
                    key={p}
                    page={p}
                    className={`shrink-0 rounded-sm border border-background-300 ${pageClass}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onPrev}
        disabled={!canPrev}
        aria-label="이전 쪽"
        className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary-700 bg-primary-900/90 text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-30 md:left-5 md:h-12 md:w-12"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={!canNext}
        aria-label="다음 쪽"
        className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-primary-700 bg-primary-900/90 text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-30 md:right-5 md:h-12 md:w-12"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true"></i>
      </button>
    </div>
  );
}